import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * ModuleManagementDialog Component
 * Dialog for creating and editing modules
 */
const ModuleManagementDialog = ({
  open,
  onClose,
  module = null,
  onSave,
  mode = 'create', // 'create' or 'edit'
  allModules = [], // List of all modules for parent selection
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    category: 'Other',
    parentModuleId: '',
  });
  const [errors, setErrors] = useState({});

  // Categories available for modules
  const categories = [
    'Sales',
    'Finance',
    'Inventory',
    'Reports',
    'Settings',
    'Users',
    'Other',
  ];

  // Reset form when dialog opens or module changes
  useEffect(() => {
    if (module && mode === 'edit') {
      setFormData({
        name: module.ModuleName || module.name || '',
        displayName: module.DisplayName || module.displayName || module.ModuleName || module.name || '',
        description: module.Description || module.description || '',
        category: module.Category || module.category || 'Other',
        parentModuleId: module.ParentModuleId || module.parentModuleId || '',
      });
    } else {
      setFormData({
        name: '',
        displayName: '',
        description: '',
        category: 'Other',
        parentModuleId: '',
      });
    }
    setErrors({});
  }, [module, mode, open]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Module name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Module name must be at least 3 characters';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    onSave({
      ...formData,
      Id: module?.Id || module?.id,
    });
  };

  const handleClose = () => {
    setFormData({
      name: '',
      displayName: '',
      description: '',
      category: 'Other',
      parentModuleId: '',
    });
    setErrors({});
    onClose();
  };

  // Filter out current module and its descendants from parent options
  const availableParentModules = allModules.filter(m => {
    const moduleId = module?.Id || module?.id;
    // Can't be its own parent
    if (moduleId && (m.Id === moduleId || m.id === moduleId)) {
      return false;
    }
    // Can't select a child as parent (prevent circular reference)
    if (moduleId && (m.ParentModuleId === moduleId || m.parentModuleId === moduleId)) {
      return false;
    }
    return true;
  });

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mode === 'create' ? <AddIcon /> : <EditIcon />}
            <Typography variant="h6">
              {mode === 'create' ? 'Create Module' : 'Edit Module'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
          {mode === 'create' && (
            <Alert severity="info">
              Create a new module to manage permissions for specific features
            </Alert>
          )}

          {/* Module Name */}
          <TextField
            label="Module Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name || 'Unique identifier for the module (e.g., Customers, Products)'}
            required
            fullWidth
            autoFocus
            placeholder="e.g., Customers, Products, Reports"
          />

          {/* Display Name */}
          <TextField
            label="Display Name"
            value={formData.displayName}
            onChange={handleChange('displayName')}
            error={!!errors.displayName}
            helperText={errors.displayName || 'User-friendly name shown in UI (optional)'}
            fullWidth
            placeholder="Leave empty to use Module Name"
          />

          {/* Category */}
          <TextField
            select
            label="Category"
            value={formData.category}
            onChange={handleChange('category')}
            error={!!errors.category}
            helperText={errors.category || 'Group modules by category'}
            required
            fullWidth
          >
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {category}
                  <Chip 
                    label={category} 
                    size="small" 
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {/* Parent Module */}
          <TextField
            select
            label="Parent Module"
            value={formData.parentModuleId}
            onChange={handleChange('parentModuleId')}
            helperText="Optional: Select a parent module to create a hierarchical structure"
            fullWidth
          >
            <MenuItem value="">
              <em>None (Top-level module)</em>
            </MenuItem>
            {availableParentModules.map((parentModule) => (
              <MenuItem key={parentModule.Id || parentModule.id} value={parentModule.Id || parentModule.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {parentModule.ModuleName}
                  </Typography>
                  {parentModule.Category && (
                    <Chip 
                      label={parentModule.Category} 
                      size="small" 
                      sx={{ fontSize: '0.65rem', height: '18px' }}
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {/* Description */}
          <TextField
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={3}
            fullWidth
            helperText="Optional description of the module's purpose"
          />

          {/* Preview */}
          <Box
            sx={{
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Preview
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formData.name || 'Module Name'}
            </Typography>
            {formData.displayName && formData.displayName !== formData.name && (
              <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                Display: {formData.displayName}
              </Typography>
            )}
            {formData.parentModuleId && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                Parent: {allModules.find(m => (m.Id || m.id) === formData.parentModuleId)?.ModuleName || 'Unknown'}
              </Typography>
            )}
            {formData.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {formData.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={formData.category} 
                size="small"
              />
              {formData.parentModuleId && (
                <Chip 
                  label="Sub-Module" 
                  size="small"
                  sx={{ bgcolor: '#ff980015', color: '#ff9800' }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
            },
          }}
        >
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModuleManagementDialog;
